import React, { useCallback, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppSelector } from '@/store';
import { useSendVerificationEmailMutation } from '@/store/api';
import { useToast } from '@/contexts/admin/ToastContext';
import { Toast } from '@/components/common/admin/mobile/Toast';
import { ACCOUNT_COLORS } from '@/constants/residents/mobile/accountColors';
import { accountSectionStyles } from '@/constants/residents/mobile/accountSectionStyles';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SVG_COMPONENTS } from '@/assets/admin';
import { strings, formatString } from '@/constants/residents/strings';
import { useLocalToast } from '@/hooks/common/useLocalToast';

const ChevronRightIcon = RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT;
const MailIcon = SVG_COMPONENTS.SEND_INVITATION;

/**
 * Mobile Reset Password Section
 * Displays reset password instructions and button
 * Based on Figma design "3. Password"
 */
export const MobileResetPasswordSection: React.FC = () => {
  const insets = useSafeAreaInsets();
  const { showError } = useToast();
  const user = useAppSelector((s) => (s as any).auth?.user);
  const email = user?.email || '';
  
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const { toast, showToast, hideToast, resetToast } = useLocalToast();
  const [sendVerificationEmail, { isLoading }] = useSendVerificationEmailMutation();

  const handleResetPassword = useCallback(async () => {
    if (!email) {
      showError('No email associated with this account.');
      return;
    }

    try {
      await sendVerificationEmail({ 
        email, 
        targetPath: '/account/account-reset-password/success' 
      }).unwrap();
      
      // Show verification modal instead of navigating away
      setShowVerificationModal(true);
    } catch (_e) {
      showError('Failed to send verification email');
    }
  }, [email, sendVerificationEmail, showError]);

  const handleResendEmail = useCallback(async () => {
    if (!email) return;
    
    try {
      await sendVerificationEmail({ 
        email, 
        targetPath: '/account/account-reset-password/success' 
      }).unwrap();
      // Use local toast for modal
      showToast('success', 'Verification email resent');
    } catch (_e) {
      showToast('error', 'Failed to resend verification email');
    }
  }, [email, sendVerificationEmail, showToast]);

  const handleCloseModal = useCallback(() => {
    setShowVerificationModal(false);
    // Reset local toast when closing modal
    resetToast();
  }, [resetToast]);

  return (
    <View style={accountSectionStyles.container}>
      {/* Header */}
      <View style={accountSectionStyles.header}>
        <Text style={accountSectionStyles.headerTitle}>{strings.account.resetPasswordTitle}</Text>
      </View>

      {/* Content */}
      <View style={styles.content}>
        <Text style={styles.description}>
          {strings.account.resetPasswordCopy}
        </Text>

        {/* Reset Password Button */}
        <TouchableOpacity
          style={accountSectionStyles.actionButton}
          onPress={handleResetPassword}
          disabled={isLoading}
          activeOpacity={0.8}
        >
          <Text style={accountSectionStyles.actionButtonText}>
            {isLoading ? 'Sending...' : strings.account.resetPasswordButton}
          </Text>
          
          <View style={styles.buttonRight}>
            {isLoading ? (
              <ActivityIndicator size="small" color={ACCOUNT_COLORS.textPrimary} />
            ) : (
              <ChevronRightIcon width={24} height={24} color={ACCOUNT_COLORS.textPrimary} />
            )}
          </View>
        </TouchableOpacity>
      </View>

      {/* Verification Email Sent Modal */}
      <Modal
        visible={showVerificationModal}
        animationType="fade"
        transparent={false}
        onRequestClose={handleCloseModal}
      >
        <View style={[accountSectionStyles.modalContainer, { paddingTop: insets.top }]}>
          <View style={accountSectionStyles.modalContent}>
            <View style={accountSectionStyles.modalBodyWide}>
              {/* Mail Icon */}
              <View style={accountSectionStyles.modalIconContainerLarge}>
                {MailIcon && (
                  <MailIcon width={64} height={64} color={ACCOUNT_COLORS.textSecondary} />
                )}
              </View>

              {/* Title */}
              <Text style={accountSectionStyles.modalTitle}>
                {strings.account.resetPasswordModalTitle}
              </Text>

              {/* Message */}
              <Text style={accountSectionStyles.modalMessageSmall}>
                {formatString(strings.account.resetPasswordModalMessagePrefix, email)}
              </Text>
            </View>

            {/* Buttons */}
            <View style={[accountSectionStyles.modalFooter, { paddingBottom: Math.max(insets.bottom, 24) }]}>
              <TouchableOpacity
                style={accountSectionStyles.cancelButton}
                onPress={handleResendEmail}
                disabled={isLoading}
              >
                <Text style={accountSectionStyles.cancelButtonText}>
                  {isLoading ? 'Sending...' : strings.account.resetPasswordModalResend}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={accountSectionStyles.confirmButton}
                onPress={handleCloseModal}
              >
                <Text style={accountSectionStyles.confirmButtonText}>
                  {strings.account.resetPasswordModalClose}
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Toast rendered inside modal so it appears above */}
          <Toast
            type={toast.type}
            message={toast.message}
            visible={toast.visible}
            onHide={hideToast}
            autoHide={true}
            duration={4000}
            position="top"
          />
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    padding: 20,
    gap: 16,
  },
  description: {
    fontSize: 16,
    fontWeight: '400',
    color: ACCOUNT_COLORS.textPrimary,
    lineHeight: 24,
  },
  buttonRight: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: 28,
  },
});

export default MobileResetPasswordSection;
